import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, FileWarning, CheckCircle2, Loader2, Scale, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayIso = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

const WithdrawalPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    email: '',
    order_number: '',
    purchase_date: '',
    reason: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useDocumentMeta({
    title: 'Elállási Nyilatkozat (14 nap) | Orosz Coaching',
    description:
      '14 napos elállási nyilatkozat digitális benyújtása a 45/2014. (II. 26.) Korm. rendelet alapján – Orosz Coaching.',
    canonical: `${typeof window !== 'undefined' ? window.location.origin : ''}/elallasi-nyilatkozat`,
    type: 'website',
  });

  const update = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error('Adj meg egy érvényes nevet.');
    if (!form.email.includes('@'))     return toast.error('Adj meg egy érvényes e-mail címet.');
    if (form.order_number.trim().length < 2) return toast.error('Add meg a rendelés- vagy számlaszámot.');
    if (!form.purchase_date)           return toast.error('Add meg a vásárlás dátumát.');

    setSubmitting(true);
    try {
      await axios.post(`${API}/withdrawal/submit`, {
        name:          form.name.trim(),
        email:         form.email.trim(),
        order_number:  form.order_number.trim(),
        purchase_date: form.purchase_date,
        reason:        form.reason.trim() || null,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'A nyilatkozat elküldése sikertelen.';
      toast.error(typeof detail === 'string' ? detail : 'Próbáld újra.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-28 pb-24 container mx-auto px-6 max-w-2xl text-center" data-testid="withdrawal-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-yellow-500/10 grid place-items-center mb-6">
            <CheckCircle2 size={32} className="text-yellow-600" />
          </div>
          <h1
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Elállási nyilatkozatod beérkezett
          </h1>
          <p className="text-base text-gray-600 leading-relaxed mb-6">
            3 munkanapon belül felülvizsgáljuk a kérelmet és felvesszük veled a kapcsolatot.
            Az esetleges visszatérítés a 45/2014. Korm. rendelet alapján 14 napon belül történik
            ugyanarra a bankkártyára, amellyel a fizetés történt.
          </p>
          <p className="text-sm text-gray-500 mb-10">
            Egy megerősítő e-mailt is küldtünk a{' '}
            <strong className="text-gray-900">{form.email}</strong> címre.
          </p>
          <Button
            data-testid="withdrawal-back-home"
            onClick={() => navigate('/')}
            className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold"
          >
            Vissza a főoldalra
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="pt-28 pb-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-2xl">
          <Link
            to="/aszf"
            data-testid="withdrawal-back-link"
            className="inline-flex items-center text-sm text-yellow-700 hover:text-yellow-600 mb-6 transition-colors"
          >
            <ArrowLeft size={14} className="mr-1.5" />
            Vissza az ÁSZF-hez
          </Link>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <FileWarning size={22} className="text-yellow-600" />
              <span className="text-xs font-bold tracking-widest uppercase text-yellow-700">
                Fogyasztói jog · 45/2014. (II. 26.) Korm. rendelet
              </span>
            </div>
            <h1
              className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              14 napos Elállási Nyilatkozat
            </h1>
            <p className="text-base text-gray-600 leading-relaxed">
              A digitális szolgáltatási csomag (online coaching, edzés- és táplálkozási tervek)
              megvásárlásától számítva <strong>14 nap áll rendelkezésedre az elállásra</strong>,
              amennyiben a személyes edzés vagy aktív online coaching még nem kezdődött meg.
              Töltsd ki az alábbi űrlapot a digitális kérelem benyújtásához.
            </p>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-4 mb-8 flex items-start gap-3">
            <Scale className="text-yellow-700 shrink-0 mt-0.5" size={18} />
            <p className="text-xs text-yellow-900 leading-relaxed">
              Amennyiben kifejezetten kérted a szolgáltatás 14 napon belüli megkezdését, és időközben
              edzéstervet kaptál vagy aktív edzést végeztél, a már teljesített, arányos szolgáltatási
              díj megfizetésére kötelezett vagy — a fennmaradó rész visszatérítésre kerül.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="withdrawal-form"
            className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8 space-y-5"
          >
            <div>
              <Label htmlFor="w-name" className="text-sm font-semibold text-gray-900">Teljes név *</Label>
              <Input
                id="w-name"
                data-testid="withdrawal-name"
                required
                value={form.name}
                onChange={update('name')}
                className="mt-2 h-11"
                placeholder="Pl. Kovács János"
              />
            </div>
            <div>
              <Label htmlFor="w-email" className="text-sm font-semibold text-gray-900">E-mail (a vásárláshoz használt) *</Label>
              <Input
                id="w-email"
                data-testid="withdrawal-email"
                type="email"
                required
                value={form.email}
                onChange={update('email')}
                className="mt-2 h-11"
                placeholder="vasarlo@example.com"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="w-order" className="text-sm font-semibold text-gray-900">Rendelés- vagy számlaszám *</Label>
                <Input
                  id="w-order"
                  data-testid="withdrawal-order"
                  required
                  value={form.order_number}
                  onChange={update('order_number')}
                  className="mt-2 h-11"
                  placeholder="E-GO-2026-…"
                />
              </div>
              <div>
                <Label htmlFor="w-date" className="text-sm font-semibold text-gray-900">Vásárlás dátuma *</Label>
                <Input
                  id="w-date"
                  data-testid="withdrawal-date"
                  type="date"
                  required
                  max={todayIso()}
                  value={form.purchase_date}
                  onChange={update('purchase_date')}
                  className="mt-2 h-11"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="w-reason" className="text-sm font-semibold text-gray-900">
                Indoklás <span className="text-gray-400 font-normal">(opcionális)</span>
              </Label>
              <Textarea
                id="w-reason"
                data-testid="withdrawal-reason"
                rows={3}
                value={form.reason}
                onChange={update('reason')}
                className="mt-2"
                placeholder="Pl. a programom megváltozott / nem indítom el a coachingot…"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Az indoklás megadása nem kötelező, de gyorsítja a folyamatot.
              </p>
            </div>

            <div className="pt-3">
              <Button
                type="submit"
                data-testid="withdrawal-submit"
                disabled={submitting}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold h-13 text-base rounded-xl shadow-lg shadow-yellow-500/20 py-6"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={18} />
                    Küldés folyamatban...
                  </>
                ) : (
                  'Elállási nyilatkozat benyújtása'
                )}
              </Button>
              <p className="text-center text-xs text-gray-500 mt-3 inline-flex items-center justify-center gap-1.5 w-full">
                <ShieldCheck size={12} className="text-yellow-600" />
                Küldés után visszaigazoló e-mailt kapsz a megadott címre.
              </p>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WithdrawalPage;
